import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Skeleton,
} from "@mui/material";
import axios from "axios";

// Web3 & Blockchain Services
import cinstance from "../../../Service/randomcinstance";
import web3 from "../../../Service/web3";
import { networkID, safeFromWei } from "../../../../static/utils";
import {
  Path,
  getNetworkCurrency,
  DEFAULT_CURRENCY,
  LOTTERY_NAMES,
} from "../../../../static/Constants";
import getImageUrlByHash from "../../../../static/logoUtils";
import {
  getConnectedAccount,
  connectWallet as triggerConnectWallet,
  subscribeToWalletEvents,
} from "../../../../static/walletUtils";

// Common UI Components
import NavBar from "../../../Components/NavBar";
import FooterStrip from "../../../Components/footer/FooterStrip";
import {
  SuccessPopup,
  FailedPopup,
  CommonBreadcrumbs,
  toast,
} from "../../../Components/ui";
import { colors, borderRadiusPx } from "../../../../theme";

// Missile Lottery Subcomponents
import {
  MissileHeader,
  MissileVisualStage,
  MissileKeypadDeck,
  MissileSummaryCard,
  MissileHowToPlay,
  MissileEmptyState,
  MISSILE_TIERS,
} from "./components";

import "./MissileLotteryStyle.css";

const withTimeout = (promise, ms = 5000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("RPC Request Timeout")), ms)
    ),
  ]);
};

const MissileLottery = (props) => {
  const params = useParams();
  const urlId = params?.id || props?.match?.params?.id;

  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(false);
  const [account, setAccount] = useState("");
  const [networkId, setNetworkId] = useState(networkID);

  const [lotterylist, setLotteryList] = useState(null);
  const [datelist, setDateList] = useState(null);
  const [partnerDetails, setPartnerDetails] = useState(null);
  const [partnerLogoData, setPartnerLogoData] = useState([]);

  // Selection & UI States
  const [selectedMissileNumber, setSelectedMissileNumber] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailed, setShowFailed] = useState(false);
  const [successMessage, setSuccessMessage] = useState(
    "Rocket launched successfully on-chain!"
  );

  const apiUrl = process.env.REACT_APP_API_KEY;
  const currency = useMemo(
    () => getNetworkCurrency(networkId) || DEFAULT_CURRENCY,
    [networkId]
  );

  // Initialize Account & Listeners
  const checkAccount = useCallback(async () => {
    try {
      const acc = await getConnectedAccount();
      setAccount(acc || "");
      if (window.ethereum) {
        const netId =
          window.ethereum.networkVersion ||
          (web3 && web3.eth && web3.eth.net
            ? await web3.eth.net.getId().catch(() => null)
            : null);
        if (netId) setNetworkId(netId);
      }
    } catch (err) {
      console.warn("Could not check connected account:", err);
    }
  }, []);

  const connectWallet = async () => {
    const res = await triggerConnectWallet();
    if (res.account) {
      setAccount(res.account);
    } else if (res.error === "NO_WALLET") {
      toast.error(res.message);
    } else if (res.isPending) {
      toast.info(res.message);
    } else if (!res.isCancelled && res.message) {
      toast.error(res.message);
    }
  };

  // Fetch Lottery Details with Watchdog Timeout
  const fetchLotteryData = useCallback(async () => {
    setLoading(true);

    const watchdog = setTimeout(() => {
      setLoading(false);
    }, 6000);

    try {
      if (window.ethereum) {
        window.web3 = web3;
      }

      let activeId = urlId;

      // Auto-discover active RocketBET lottery if no ID is present in URL
      if (!activeId && cinstance && cinstance.methods) {
        try {
          const totalRaw = await withTimeout(
            cinstance.methods.lotteryId().call(),
            3500
          );
          const total = Number(totalRaw || 0);

          for (let i = total; i >= 1; i--) {
            try {
              const item = await withTimeout(
                cinstance.methods.lottery(i).call(),
                2000
              );
              if (
                item &&
                item.lotteryId &&
                item.lotteryId !== "0" &&
                (item.status === "0" || item.status === "5") &&
                Number(item.lotteryType) === 3
              ) {
                activeId = `${i}`;
                break;
              }
            } catch {
              // Ignore single scan error
            }
          }
        } catch (findErr) {
          console.warn("Could not auto-find active missile lottery:", findErr);
        }
      }

      if (!activeId) {
        setLotteryList(null);
        setLoading(false);
        clearTimeout(watchdog);
        return;
      }

      // 1. Fetch Main Lottery Info
      const lotteryRes = await withTimeout(
        cinstance.methods.lottery(activeId).call(),
        4500
      ).catch((err) => {
        console.error("Error fetching missile lottery info:", err);
        return null;
      });

      if (!lotteryRes || !lotteryRes.lotteryId || lotteryRes.lotteryId === "0") {
        setLotteryList(null);
        setLoading(false);
        clearTimeout(watchdog);
        return;
      }

      setLotteryList(lotteryRes);

      // 2. Fetch Dates & Level
      try {
        const datesRes = await withTimeout(
          cinstance.methods.lotteryDates(activeId).call(),
          3500
        );
        setDateList(datesRes);
      } catch (dErr) {
        console.warn("Error fetching missile lottery dates:", dErr);
      }

      // 3. Fetch Partner Info
      if (
        lotteryRes.partnerAddress &&
        lotteryRes.partnerAddress !==
          "0x0000000000000000000000000000000000000000" &&
        lotteryRes.partnerAddress !== "0x0"
      ) {
        try {
          const partnerRes = await withTimeout(
            cinstance.methods.partnerbyaddr(lotteryRes.partnerAddress).call(),
            3500
          );
          setPartnerDetails(partnerRes);
        } catch (pErr) {
          console.warn("Error fetching partner details:", pErr);
        }
      }
    } catch (err) {
      console.error("Error loading missile lottery page:", err);
      setLotteryList(null);
    } finally {
      clearTimeout(watchdog);
      setLoading(false);
    }
  }, [urlId]);

  // Fetch S3 Partner Logos
  useEffect(() => {
    let isCancelled = false;
    if (apiUrl) {
      axios
        .get(`${apiUrl}/s3imageupload/getAllPartnerLogos`)
        .then((response) => {
          if (!isCancelled) {
            const data = response?.data?.result || response?.data || [];
            setPartnerLogoData(Array.isArray(data) ? data : []);
          }
        })
        .catch((err) => {
          console.warn("Partner logos fetch error:", err);
          if (!isCancelled) setPartnerLogoData([]);
        });
    }
    return () => {
      isCancelled = true;
    };
  }, [apiUrl]);

  useEffect(() => {
    checkAccount();
    fetchLotteryData();

    const unsubscribe = subscribeToWalletEvents({
      onAccountsChanged: (acc) => {
        setAccount(acc || "");
      },
      onChainChanged: () => {
        checkAccount();
        fetchLotteryData();
      },
      onDisconnect: () => setAccount(""),
    });

    return unsubscribe;
  }, [checkAccount, fetchLotteryData]);

  // Quick Pick Random Target Generator
  const handleQuickPick = () => {
    const pickCount = Number(lotterylist?.pickNumbers) || 3;
    let randomStr = "";
    for (let i = 0; i < pickCount; i++) {
      randomStr += Math.floor(Math.random() * 10).toString();
    }
    setSelectedMissileNumber(randomStr);
    toast.info(`Random Target ${randomStr} generated!`);
  };

  // Buy / Launch Missile Action
  const handleBuy = async () => {
    if (!window.ethereum) {
      toast.error("Web3 wallet not detected.");
      return;
    }

    const pickCount = Number(lotterylist?.pickNumbers) || 3;
    if (!selectedMissileNumber || selectedMissileNumber.length !== pickCount) {
      toast.warning(`Please enter exactly ${pickCount} digits.`);
      return;
    }

    try {
      setTxLoading(true);
      window.web3 = web3;
      const accounts = await web3.eth.getAccounts();

      if (!accounts || accounts.length === 0) {
        await connectWallet();
        setTxLoading(false);
        return;
      }

      const activeId = lotterylist?.lotteryId || urlId;

      await cinstance.methods
        .buyMissilelottery(selectedMissileNumber, activeId)
        .send({
          from: accounts[0],
          value: lotterylist.entryFee,
        });

      // Post-purchase check for winner
      try {
        const checkRes = await cinstance.methods.lottery(activeId).call();
        if (
          checkRes &&
          (checkRes.lotteryWinner || "").toLowerCase() ===
            accounts[0].toLowerCase()
        ) {
          setSuccessMessage(
            "Congratulations! You are the lucky winner of the jackpot!"
          );
        } else {
          setSuccessMessage(
            "Rocket launched successfully on-chain! Best of luck!"
          );
        }
      } catch {
        setSuccessMessage("Rocket launched successfully on-chain!");
      }

      setTxLoading(false);
      setShowSuccess(true);
      toast.success("Launch transaction confirmed!");
    } catch (err) {
      console.error("Buy missile lottery error:", err);
      setTxLoading(false);
      setShowFailed(true);
      toast.error("Launch transaction was rejected or failed.");
    }
  };

  const partnerLogoUrl = useMemo(() => {
    if (!partnerDetails?.logoHash) return null;
    return getImageUrlByHash(
      partnerDetails?.logoHash,
      Array.isArray(partnerLogoData) ? partnerLogoData : []
    );
  }, [partnerDetails, partnerLogoData]);

  const totalPrizeFormatted = useMemo(() => {
    return safeFromWei(lotterylist?.totalPrize);
  }, [lotterylist?.totalPrize]);

  const entryFeeFormatted = useMemo(() => {
    return safeFromWei(lotterylist?.entryFee);
  }, [lotterylist?.entryFee]);

  const currentLevel = Math.min(
    4,
    Math.max(1, Number(datelist?.level) || 1)
  );
  const tierInfo = MISSILE_TIERS[currentLevel];
  const effectiveLotteryId = lotterylist?.lotteryId || urlId;

  return (
    <Box className="missile-lottery-page" sx={{ bgcolor: colors.slate[50], minHeight: "100vh" }}>
      <NavBar />

      <Container
        maxWidth="lg"
        sx={{
          pt: { xs: "90px", md: "110px" },
          pb: 8,
        }}
      >
        {/* Breadcrumb Navigation (Outside Card) */}
        <CommonBreadcrumbs
          items={[
            { label: "Home", path: Path.root },
            { label: "All Lotteries", path: Path.allLotteries },
            { label: LOTTERY_NAMES.ROCKETBET, path: Path.allmissilelottery },
            {
              label: urlId
                ? `${LOTTERY_NAMES.ROCKETBET} #${urlId}`
                : LOTTERY_NAMES.ROCKETBET,
            },
          ]}
          sx={{ mb: 2.5 }}
        />

        {loading ? (
          <Box sx={{ py: 3 }}>
            <Skeleton
              variant="rounded"
              height={180}
              sx={{ mb: 3.5, borderRadius: borderRadiusPx.xxl }}
            />
            <Grid container spacing={3.5}>
              <Grid item xs={12} lg={8}>
                <Skeleton
                  variant="rounded"
                  height={320}
                  sx={{ mb: 3, borderRadius: borderRadiusPx.xl }}
                />
                <Skeleton
                  variant="rounded"
                  height={360}
                  sx={{ borderRadius: borderRadiusPx.xl }}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <Skeleton
                  variant="rounded"
                  height={480}
                  sx={{ borderRadius: borderRadiusPx.xl }}
                />
              </Grid>
            </Grid>
          </Box>
        ) : !lotterylist ||
          !lotterylist.lotteryId ||
          lotterylist.lotteryId === "0" ? (
          <MissileEmptyState
            lotteryId={urlId}
            onRetry={() => fetchLotteryData()}
          />
        ) : (
          <Box sx={{ py: 1 }}>
            {/* Standardized Missile Header */}
            <MissileHeader
              lotteryId={effectiveLotteryId}
              lottery={lotterylist}
              datelist={datelist}
              partnerDetails={partnerDetails}
              partnerLogoUrl={partnerLogoUrl}
              currency={currency}
              totalPrizeFormatted={totalPrizeFormatted}
              tierInfo={tierInfo}
            />

            {/* Main Interactive Deck & Sidebar Summary */}
            <Grid container spacing={3.5}>
              {/* Left Column: Visual Stage, Keypad, Rules */}
              <Grid item xs={12} lg={8}>
                <MissileVisualStage
                  level={datelist?.level || 1}
                  selectedNumber={selectedMissileNumber}
                  pickNumbers={lotterylist?.pickNumbers || 3}
                />

                <MissileKeypadDeck
                  pickNumbers={lotterylist?.pickNumbers || 3}
                  selectedNumber={selectedMissileNumber}
                  onSelectNumber={(val) => setSelectedMissileNumber(val)}
                  onQuickPick={handleQuickPick}
                  onClear={() => setSelectedMissileNumber("")}
                />

                <MissileHowToPlay
                  pickNumbers={lotterylist?.pickNumbers || 3}
                />
              </Grid>

              {/* Right Column: Sticky Summary & Checkout */}
              <Grid item xs={12} lg={4}>
                <MissileSummaryCard
                  lotteryId={effectiveLotteryId}
                  entryFeeFormatted={entryFeeFormatted}
                  totalPrizeFormatted={totalPrizeFormatted}
                  currency={currency}
                  pickNumbers={lotterylist?.pickNumbers || 3}
                  selectedNumber={selectedMissileNumber}
                  tierInfo={tierInfo}
                  account={account}
                  loading={txLoading}
                  onBuy={handleBuy}
                  onConnectWallet={connectWallet}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </Container>

      <FooterStrip />

      {/* Transaction Feedback Dialogs */}
      <SuccessPopup
        show={showSuccess}
        setShowModal={() => setShowSuccess(false)}
        message={successMessage}
        redirectPath={Path.profile}
        btnName="Go to Profile"
      />

      <FailedPopup
        show={showFailed}
        setShowModal={() => setShowFailed(false)}
      />
    </Box>
  );
};

export default MissileLottery;
